import type { BrandConfig, EnvField, EnvGroup, TenantAnswers } from '@api/api';

export type Errors = Record<string, string>;

export const EMPTY_TENANT: TenantAnswers = {
  brandName: '',
  currency: 'EUR',
  timezone: 'Europe/Kyiv',
  defaultCountry: '',
  phoneCountries: [],
  defaultLocale: 'en',
  locales: ['en'],
  city: { name: '', lat: 0, lon: 0, shortCode: '', locale: 'en' },
  prices: { unlock: 1, rideMinute: 0.25, pauseMinute: 0.1, reservationMinute: 0.1 },
  adminLogin: '',
};

export const EMPTY_BRAND: BrandConfig = {
  appName: '',
  riderBundleId: '',
  staffBundleId: '',
  androidPackage: '',
  appleTeamId: '',
  colors: { primary: '#2A78D6', onPrimary: '#FFFFFF' },
  supportEmail: '',
  supportPhone: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BUNDLE_RE = /^[a-zA-Z][a-zA-Z0-9-]*(\.[a-zA-Z][a-zA-Z0-9-]*)+$/;
const ANDROID_RE = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function tenantErrors(t: TenantAnswers): Errors {
  const e: Errors = {};
  if (!t.brandName.trim()) e.brandName = 'Required';
  if (!/^[A-Z]{3}$/.test(t.currency)) e.currency = 'ISO 4217 code, e.g. EUR';
  if (!t.timezone) e.timezone = 'Required';
  if (!/^[A-Z]{2}$/.test(t.defaultCountry)) e.defaultCountry = 'Two-letter code, e.g. GB';
  if (!t.phoneCountries.length) e.phoneCountries = 'At least one country';
  if (!t.defaultLocale) e.defaultLocale = 'Required';
  if (!t.locales.length) e.locales = 'At least one locale';
  else if (!t.locales.includes(t.defaultLocale)) e.locales = 'Must include the default locale';
  if (!t.city.name.trim()) e['city.name'] = 'Required';
  if (!(t.city.lat >= -90 && t.city.lat <= 90) || t.city.lat === 0) e['city.lat'] = 'Latitude −90…90';
  if (!(t.city.lon >= -180 && t.city.lon <= 180) || t.city.lon === 0) e['city.lon'] = 'Longitude −180…180';
  if (!/^[A-Z0-9]{2,5}$/.test(t.city.shortCode)) e['city.shortCode'] = '2–5 capital letters or digits';
  for (const k of ['unlock', 'rideMinute', 'pauseMinute', 'reservationMinute'] as const) {
    const v = t.prices[k];
    if (v !== undefined && (Number.isNaN(v) || v < 0)) e[`prices.${k}`] = 'Must be 0 or more';
  }
  if (t.prices.rideMinute === undefined) e['prices.rideMinute'] = 'Required';
  if (!EMAIL_RE.test(t.adminLogin) && !/^[a-z0-9._-]{3,}$/i.test(t.adminLogin)) e.adminLogin = 'E-mail or login of the first super admin';
  return e;
}

export function brandErrors(b: BrandConfig): Errors {
  const e: Errors = {};
  if (!b.appName.trim()) e.appName = 'Required';
  else if (b.appName.length > 30) e.appName = 'At most 30 characters (App Store limit)';
  if (!BUNDLE_RE.test(b.riderBundleId)) e.riderBundleId = 'Reverse-DNS, e.g. com.brand.rider';
  if (!BUNDLE_RE.test(b.staffBundleId)) e.staffBundleId = 'Reverse-DNS, e.g. com.brand.staff';
  if (b.riderBundleId && b.riderBundleId === b.staffBundleId) e.staffBundleId = 'Must differ from the rider bundle ID';
  if (!ANDROID_RE.test(b.androidPackage)) e.androidPackage = 'Lowercase reverse-DNS, e.g. com.brand.rider';
  if (!/^[A-Z0-9]{10}$/.test(b.appleTeamId)) e.appleTeamId = '10 characters from developer.apple.com';
  if (!HEX_RE.test(b.colors.primary)) e['colors.primary'] = 'Hex colour, #RRGGBB';
  if (!HEX_RE.test(b.colors.onPrimary)) e['colors.onPrimary'] = 'Hex colour, #RRGGBB';
  if (b.supportEmail && !EMAIL_RE.test(b.supportEmail)) e.supportEmail = 'Not an e-mail';
  return e;
}

// ------------------------------------------------------------------ env

export const GROUP_ORDER: EnvGroup[] = ['sms', 'payments', 'push', 'iot', 'maps', 'core', 'market', 'brand', 'weather', 'links', 'apps', 'limits', 'advanced'];
export const REQUIRED_GROUPS: EnvGroup[] = ['sms', 'payments', 'push', 'iot', 'maps'];

export const GROUP_LABEL: Record<EnvGroup, { title: string; help: string }> = {
  core: { title: 'Core and security', help: 'Secrets of the stack are generated automatically.' },
  market: { title: 'Market', help: 'Defaults come from the tenant answers.' },
  brand: { title: 'Brand', help: 'Name and colours used by the backend and CRM.' },
  sms: { title: 'SMS', help: 'Login codes for riders.' },
  payments: { title: 'Payments', help: 'Card top-ups and ride charges.' },
  push: { title: 'Push and staff alerts', help: 'Firebase Cloud Messaging and the Telegram bot for staff.' },
  iot: { title: 'IoT', help: 'Scooter telematics: lock, unlock, positions.' },
  maps: { title: 'Maps', help: 'Google Maps in the CRM; the start point defaults to the first city.' },
  weather: { title: 'Weather', help: 'Optional rain warnings.' },
  links: { title: 'Links', help: 'Legal and support pages shown in the apps.' },
  apps: { title: 'Apps', help: 'Store links and forced-update version.' },
  limits: { title: 'Limits', help: '' },
  advanced: { title: 'Advanced', help: 'Change only if you know why.' },
};

/** Current value of a field given local edits (undefined edit = unchanged). */
export function effectiveValue(f: EnvField, edits: Record<string, string | null>): string | null {
  return f.key in edits ? edits[f.key] : f.value;
}

export function isFieldSet(f: EnvField, edits: Record<string, string | null>): boolean {
  if (f.key in edits) return !!edits[f.key];
  return f.isSet;
}

/** Value of a provider selector: edit, stored value, or the platform default. */
export function selectorValue(fields: EnvField[], key: string, edits: Record<string, string | null>): string {
  const f = fields.find((x) => x.key === key);
  if (!f) return '';
  const v = key in edits ? edits[key] : f.value;
  return v || f.default || '';
}

/** Provider-dependent requirement: TWILIO_* is required when SMS_PROVIDER = twilio (EnvField.requiredWhen). */
export function isRequired(f: EnvField, fields: EnvField[], edits: Record<string, string | null>): boolean {
  if (f.required === true) return true;
  if (f.required !== 'if-provider') return false;
  if (!f.requiredWhen) return true;
  return f.requiredWhen.values.includes(selectorValue(fields, f.requiredWhen.key, edits));
}

/**
 * Required keys still without a value after the local edits. `serverMissing` (CloneDetail.missingRequired)
 * is the truth for saved values — it knows about tenant / brand fallbacks; edits are judged locally.
 */
export function missingRequired(fields: EnvField[], edits: Record<string, string | null>, serverMissing?: string[]): string[] {
  return fields
    .filter((f) => {
      if (!isRequired(f, fields, edits)) return false;
      if (f.key in edits) return !edits[f.key] && !f.default;
      if (f.isSet) return false;
      const selectorEdited = !!f.requiredWhen && f.requiredWhen.key in edits;
      if (serverMissing && !selectorEdited) return serverMissing.includes(f.key);
      return !f.default && !f.generated;
    })
    .map((f) => f.key);
}

/** Hide fields of providers that aren't selected (unless they still hold a value). */
export function isRelevant(f: EnvField, fields: EnvField[], edits: Record<string, string | null>): boolean {
  if (f.required !== 'if-provider' || !f.requiredWhen) return true;
  return isRequired(f, fields, edits) || isFieldSet(f, edits);
}
